import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import api from '../api';

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  softBorder: '#E6D8BE',
  mutedText: '#746A5B',
  success: '#1F8F4D',
  warning: '#B85C38',
  danger: '#B54135',
};

const money = value => `R${Number(value || 0).toFixed(2)}`;
const dateOnly = value => (value ? new Date(value).toLocaleDateString() : '-');

const extractLive = response => response?.data || {};

export default function LiveEarnings() {
  const [data, setData] = useState({
    riderTotals: {},
    restaurantTotals: {},
    riders: [],
    restaurants: [],
    orders: [],
  });
  const [view, setView] = useState('riders');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadEarnings = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getLiveEarnings();
      setData(extractLive(response));
    } catch (err) {
      setError(err.message || 'Could not load live earnings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEarnings();
  }, []);

  const rows = view === 'riders' ? data.riders || [] : data.restaurants || [];
  const totals = view === 'riders' ? data.riderTotals || {} : data.restaurantTotals || {};

  const orderRows = useMemo(() => (data.orders || []).slice(0, 20), [data.orders]);

  return (
    <Layout>
      <PageHeader
        eyebrow="Live Earnings"
        title="Earned Totals"
        subtitle="Track rider and restaurant earnings immediately from completed orders, before the Friday payout week closes."
        action={
          <button type="button" style={styles.refreshButton} onClick={loadEarnings} disabled={loading}>
            Refresh
          </button>
        }
      />

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <span>Total Rider Earned</span>
          <strong>{money(data.riderTotals?.earned)}</strong>
          <small>Unpaid {money(data.riderTotals?.unpaid)}</small>
        </div>
        <div style={styles.summaryCard}>
          <span>Rider Tips</span>
          <strong>{money(data.riderTotals?.tips)}</strong>
          <small>{data.riderTotals?.deliveries || 0} deliveries</small>
        </div>
        <div style={styles.summaryCard}>
          <span>Total Restaurant Earned</span>
          <strong>{money(data.restaurantTotals?.earned)}</strong>
          <small>Unpaid {money(data.restaurantTotals?.unpaid)}</small>
        </div>
        <div style={styles.summaryCard}>
          <span>Restaurant Sales</span>
          <strong>{money(data.restaurantTotals?.grossSales)}</strong>
          <small>{data.restaurantTotals?.orders || 0} orders</small>
        </div>
      </div>

      <div style={styles.segment}>
        {[
          ['riders', 'Riders'],
          ['restaurants', 'Restaurants'],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            style={{
              ...styles.segmentButton,
              ...(view === key ? styles.segmentActive : {}),
            }}>
            {label}
          </button>
        ))}
      </div>

      <section style={styles.panel}>
        <div style={styles.panelTop}>
          <div>
            <h3 style={styles.panelTitle}>{view === 'riders' ? 'Rider Earnings' : 'Restaurant Earnings'}</h3>
            <p style={styles.panelSub}>
              Earned {money(totals.earned)} | Unpaid {money(totals.unpaid)}
            </p>
          </div>
        </div>

        <div style={styles.tableShell}>
          <table style={styles.table}>
            <thead>
              {view === 'riders' ? (
                <tr>
                  <th style={styles.th}>Rider</th>
                  <th style={styles.th}>Deliveries</th>
                  <th style={styles.th}>Delivery Fees</th>
                  <th style={styles.th}>Tips</th>
                  <th style={styles.th}>Earned</th>
                  <th style={styles.th}>Unpaid</th>
                </tr>
              ) : (
                <tr>
                  <th style={styles.th}>Restaurant</th>
                  <th style={styles.th}>Orders</th>
                  <th style={styles.th}>Gross Sales</th>
                  <th style={styles.th}>Platform Fees</th>
                  <th style={styles.th}>Earned</th>
                  <th style={styles.th}>Unpaid</th>
                </tr>
              )}
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={styles.emptyCell}>Loading live earnings...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6" style={styles.emptyCell}>No completed order earnings found.</td></tr>
              ) : view === 'riders' ? (
                rows.map(row => (
                  <tr key={row.id}>
                    <td style={styles.td}><strong>{row.name}</strong><div style={styles.muted}>{row.phone || row.email || '-'}</div></td>
                    <td style={styles.td}>{row.deliveries}</td>
                    <td style={styles.td}>{money(row.deliveryFees)}</td>
                    <td style={styles.td}>{money(row.tips)}</td>
                    <td style={styles.td}><strong>{money(row.earned)}</strong></td>
                    <td style={styles.td}><strong>{money(row.unpaid)}</strong></td>
                  </tr>
                ))
              ) : (
                rows.map(row => (
                  <tr key={row.id}>
                    <td style={styles.td}><strong>{row.name}</strong><div style={styles.muted}>{row.phone || row.ownerEmail || '-'}</div></td>
                    <td style={styles.td}>{row.orders}</td>
                    <td style={styles.td}>{money(row.grossSales)}</td>
                    <td style={styles.td}>{money(row.platformFees)}</td>
                    <td style={styles.td}><strong>{money(row.earned)}</strong></td>
                    <td style={styles.td}><strong>{money(row.unpaid)}</strong></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section style={styles.panel}>
        <div style={styles.panelTop}>
          <div>
            <h3 style={styles.panelTitle}>Recent Completed Order Details</h3>
            <p style={styles.panelSub}>Latest completed orders used in the live totals.</p>
          </div>
        </div>

        <div style={styles.tableShell}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Order</th>
                <th style={styles.th}>Completed</th>
                <th style={styles.th}>Rider</th>
                <th style={styles.th}>Rider Earned</th>
                <th style={styles.th}>Restaurant</th>
                <th style={styles.th}>Restaurant Earned</th>
              </tr>
            </thead>
            <tbody>
              {orderRows.length === 0 ? (
                <tr><td colSpan="6" style={styles.emptyCell}>No completed orders found.</td></tr>
              ) : (
                orderRows.map(order => (
                  <tr key={order.id}>
                    <td style={styles.td}>#{order.id}</td>
                    <td style={styles.td}>{dateOnly(order.completedAt)}</td>
                    <td style={styles.td}>{order.rider?.name || '-'}</td>
                    <td style={styles.td}>{money(order.riderEarnings?.amount)}</td>
                    <td style={styles.td}>{order.restaurant?.name || '-'}</td>
                    <td style={styles.td}>{money(order.restaurantEarnings?.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}

const styles = {
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 14,
    marginBottom: 18,
  },
  summaryCard: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 16,
    color: COLORS.forestGreen,
    display: 'grid',
    gap: 6,
  },
  segment: {
    display: 'inline-flex',
    backgroundColor: '#EFE6D3',
    padding: 4,
    borderRadius: 8,
    border: `1px solid ${COLORS.softBorder}`,
    marginBottom: 16,
  },
  segmentButton: {
    border: 'none',
    backgroundColor: 'transparent',
    color: COLORS.mutedText,
    borderRadius: 6,
    padding: '9px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  segmentActive: {
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
  },
  panel: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    padding: 18,
    marginBottom: 20,
    boxShadow: '0 8px 24px rgba(15,61,46,0.08)',
  },
  panelTop: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  panelTitle: { margin: 0, color: COLORS.forestGreen },
  panelSub: { margin: '5px 0 0', color: COLORS.mutedText },
  refreshButton: {
    border: 'none',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    borderRadius: 8,
    padding: '10px 14px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: '1px solid #F4B8B4',
    borderRadius: 8,
    padding: 12,
    marginBottom: 14,
    fontWeight: 800,
  },
  tableShell: {
    border: `1px solid ${COLORS.softBorder}`,
    borderRadius: 8,
    overflowX: 'auto',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    backgroundColor: '#F1E7D2',
    color: COLORS.forestGreen,
    textAlign: 'left',
    padding: '13px 14px',
    fontSize: 12,
    textTransform: 'uppercase',
  },
  td: {
    padding: '14px',
    color: COLORS.forestGreen,
    borderTop: `1px solid ${COLORS.softBorder}`,
    verticalAlign: 'top',
  },
  muted: { color: COLORS.mutedText, fontSize: 12, marginTop: 4 },
  emptyCell: { padding: 22, textAlign: 'center', color: COLORS.mutedText },
};
